import { Award, Ban, Bookmark, LucideIcon, Meh, Megaphone, ThumbsUp } from 'lucide-react';
import { RecommendationStatus } from './types';

export const FOLLOW_PRESET_CONFIG: Record<string, {
  label: string;
  description: string;
  Icon: LucideIcon;
}> = {
  TOP_PICKS: {
    label: 'Top Picks Only',
    description: 'Seal of Approval only',
    Icon: Award
  },
  RECOMMENDATIONS: {
    label: 'Recommendations',
    description: 'Seal of Approval + Recommended',
    Icon: ThumbsUp
  },
  EVERYTHING: {
    label: 'Everything',
    description: 'All shouts',
    Icon: Megaphone
  }
};

// 15 seconds
export const AUTH_TIMEOUT_MS = 15000;

// Username Validation
// Only lowercase letters, numbers, and underscores
export const USERNAME_REGEX = /^[a-z0-9_]+$/;
export const INVALID_USERNAME_CHARS_REGEX = /[^a-z0-9_]/g;


// Fallback bounds for rendering without Google Maps (approx Central London)
export const FALLBACK_CAM_BOUNDS = {
  minLat: 51.5000,
  maxLat: 51.5300,
  minLng: -0.1600,
  maxLng: -0.1000
};

// Status is the single source of truth for verdict colour, glyph and copy.
// Colours are token classes (see design/tokens.css + design/tailwind.config.js):
//   color       -> solid fill (pins, selected states)
//   textColor   -> the darker "ink" variant, for text on cream/white
//   cssVar      -> raw custom property, for inline styles (borders, tints)
export interface StatusConfig {
  label: string;
  color: string;
  textColor: string;
  borderColor: string;
  cssVar: string;
  Icon: LucideIcon;
}

export const STATUS_CONFIG: Record<RecommendationStatus, StatusConfig> = {
  [RecommendationStatus.RECOMMENDED]: {
    label: 'Recommended',
    color: 'bg-status-recommend',
    textColor: 'text-status-recommend-ink',
    borderColor: 'border-status-recommend',
    cssVar: '--status-recommend',
    Icon: ThumbsUp,
  },
  [RecommendationStatus.SEAL_OF_APPROVAL]: {
    label: 'Seal of Approval',
    color: 'bg-status-seal',
    textColor: 'text-status-seal-ink',
    borderColor: 'border-status-seal',
    cssVar: '--status-seal',
    Icon: Award,
  },
  [RecommendationStatus.ITS_ALRIGHT]: {
    label: "It's Alright",
    color: 'bg-status-alright',
    textColor: 'text-status-alright-ink',
    borderColor: 'border-status-alright',
    cssVar: '--status-alright',
    Icon: Meh,
  },
  [RecommendationStatus.DO_NOT_RECOMMEND]: {
    label: 'Avoid',
    color: 'bg-status-avoid',
    textColor: 'text-status-avoid-ink',
    borderColor: 'border-status-avoid',
    cssVar: '--status-avoid',
    Icon: Ban,
  },
  [RecommendationStatus.WANT_TO_VISIT]: {
    label: 'Want to Visit',
    color: 'bg-status-want',
    textColor: 'text-status-want-ink',
    borderColor: 'border-status-want',
    cssVar: '--status-want',
    Icon: Bookmark,
  },
};

export const MAP_STYLES: google.maps.MapTypeStyle[] = [
  // --- Base: warm paper ---
  { elementType: 'geometry', stylers: [{ color: '#F7F1E6' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#374151' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#FEFBF6' }, { weight: 3 }] },

  // --- Administrative: faint boundaries, keep place names ---
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#E4DACB' }, { visibility: 'simplified' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: '#6B7280' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#374151' }] },

  // --- Land ---
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#F7F1E6' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#F2EBE0' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#F0EBDD' }] },

  // --- POIs off. Order matters: the generic `poi` rules must come BEFORE the
  //     `poi.park` rules, otherwise they overwrite the sage greenery. ---
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#EFE8DA' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },

  // --- Greenery: soft sage, with park names kept ---
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#DCE7D2' }] },
  { featureType: 'poi.park', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6E8A63' }] },
  { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#FEFBF6' }, { weight: 3 }] },
  { featureType: 'landscape.natural.terrain', elementType: 'geometry', stylers: [{ color: '#DCE7D2' }] },

  // --- Roads: off-white ribbons with a faint warm stroke ---
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FFFFFF' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#E4DACB' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6B7280' }] },
  { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#FBF7F0' }] },
  { featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ color: '#EADFCF' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#FFFFFF' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#E4DACB' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#F2EBE0' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#D9CDB8' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#EFE5D5' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry.stroke', stylers: [{ color: '#D9CDB8' }] },

  // --- Transit off ---
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },

  // --- Water: soft desaturated blue-grey ---
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#C9D6E3' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7C93A8' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#C9D6E3' }, { weight: 2 }] },
];